import { fromJS, getIn, isImmutable } from 'immutable';

import { selectPropertyObject, populateMissingSchemaValues } from '@pearsontechnology/aero-plugin-schema';
import { recordEvent, silentLog } from '@pearsontechnology/bronte-lib-fullstory';
import { composeState } from 'utils/scope/composeState';
import { getDataFromScope } from 'utils/scope/getDataFromScope';
import { mapExternalUrnToURL } from 'utils/mapUrnToUrl';
import {
  getBoundReferences,
  resolveBoundReferenceByTypeAndSourceId,
} from 'utils/resolveBoundReferences';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';

import { logNewrelicAddPageAction } from '../../cookies/NewRelic';
import StateProvider from '../../configState/StateProvider';
import { decomposeState } from '../../utils/scope/decomposeState';
import CoursewareItemResolver from './CoursewareItemResolver';
import { parseWalkableData } from './graphqlHelpers';
import { WALKABLES, ACTIVITY, INTERACTIVE, COMPONENT } from './coursewareTypes';
import {
  getDefaultActivity,
  getActivityById,
  getStaticData,
  getStaticDataElementId,
  getStaticDataDefault,
  getLearnerDataElementId,
  getLearnerDataDefaultFirst,
  getActivityByIdLearner,
  getInteractiveById,
  getInteractiveByIdLearner,
  getCompletedWalkableByEvaluationId,
  getLearnerData,
  getStaticDataFromSocket,
  getLearnerDataFromSocket,
} from '../walkable';
import CompetencyProvider from '../competency/CompetencyProvider';
import RouteProvider from '../route/RouteProvider';
import CoursewareTargetProvider from '../coursewareTarget/CoursewareTargetProvider';
import ToastManager from '../../ToastManager';
import HistoryProvider from '../history/HistoryProvider';
import SubscriptionAggregation from './SubscriptionAggregation';
import gtmService from '../../utils/gtm/gtmService';
import { httpErrorCodes } from '../../utils/gtm/constants';
import {
  STATE,
  THEME,
  SCHEMA,
  SCOPE_URN,
  CONFIG,
  PROGRESS,
  PATHWAY,
  COMPONENTS,
  ANCESTRY,
  COURSEWARE_ITEM,
  URN_EXTERNAL,
  ASSETS,
  SCOPE,
  VALUE_BIND_TYPE,
  RAW_CONFIG,
  ERROR_EVALUATION_ALREADY_COMPLETED,
} from './constants';
import TimestampManager from './TimestampManager';
import { unsubscribeEventByCoursewareId, publishEvent } from '../EventService/EventServiceProvider';
import { getSchemaVersion } from '../../utils/Schema';
import messages from './messages';


const CoursewareItemProvider = (() => {
  const _state = new StateProvider();
  const _rtmUnsubscribeFunctions = {};
  let _subscriptionAggregation;
  let _productId = null;
  let currentCohortId = null;
  let currentDeploymentId = null;

  function _saveCompetencies(id, coursewareItem) {
    if (!coursewareItem.linkedDocumentItems) {
      return;
    }

    const documentItems = coursewareItem.linkedDocumentItems.edges.map(({ node }) => ({
      itemId: node.id,
      documentId: node.documentId,
    }));
    CompetencyProvider.addCompetenciesForCoursewareItem(documentItems, id);
  }

  function _saveCoursewareItemMapToState(coursewareItemMap) {
    Object.entries(coursewareItemMap).forEach(([id, coursewareItem]) => {
      const { entryPoint, elementType, linkedDocumentItems } = coursewareItem;
      // If we know the elementType we can use that to cache coursewareTargets
      if (elementType) {
        CoursewareTargetProvider.saveCoursewareType({ id, ...coursewareItem });
      }

      // Store competencies separately if included
      if (linkedDocumentItems) {
        _saveCompetencies(id, coursewareItem);
      }

      // Don't override any existing element state with an incomplete 'partial' elements.
      // We use the entryPoint for checking, since we only includes these props on full queries
      if (entryPoint) {
        _state.set(id, coursewareItem);
      }
    });
  }

  function getUpdatedPathwaysOnProgressCompletion(walkableDataLearner) {
    const { pathways } = walkableDataLearner;
    if (walkableDataLearner.progress?.completion?.value === 1) {
      return pathways.map((pathway) => ({
        ...pathway,
        walkables: pathway?.type === 'FREE'
          ? pathway.walkables
          : { ...pathway.walkables, edges: [] }, // Clear the Graph and Linear pathways
      }));
    }

    return pathways;
  }

  async function fetchLearnerActivity(activityId, walkable) {
    const { data } = await getActivityByIdLearner({
      cohortId: currentCohortId,
      deploymentId: currentDeploymentId,
      activityId,
    });
    try {
      let { activity: walkableDataLearner } = data.learn.cohort.deployment[0];
      walkableDataLearner = { ...walkableDataLearner, progress: { ...walkableDataLearner?.progress } };
      // move the score object outside of progress object
      let walkableDataTarget = {};
      if (walkableDataLearner?.progress?.score) {
        const score = { score: walkableDataLearner.progress.score };
        // delete score object inside progress object
        delete walkableDataLearner.progress.score;
        // combine score object and rest of learner object
        walkableDataTarget = { ...walkableDataLearner, ...score };
      } else {
        walkableDataTarget = walkableDataLearner;
      }
      const pathways = getUpdatedPathwaysOnProgressCompletion(walkableDataTarget);
      const updatedWalkableDataLearner = { ...walkableDataTarget, pathways };
      const coursewareItemMap = await parseWalkableData({ ...walkable, ...updatedWalkableDataLearner });
      _saveCoursewareItemMapToState(coursewareItemMap);
      if (coursewareItemMap) {
        createBoundedListAndCreateListeners(coursewareItemMap);
      }
    } catch (parsingError) {
      throw new Error('Failed to parse learner ACTIVITY.');
    }
  }

  function fetchLearnerActivityNew(activityId, walkable) {
    getActivityByIdLearner({
      cohortId: currentCohortId,
      deploymentId: currentDeploymentId,
      activityId,
    })
      .then(async (response) => {
        const { data } = response;
        const { activity: walkableDataLearner } = data.learn.cohort.deployment[0];
        const pathways = getUpdatedPathwaysOnProgressCompletion(walkableDataLearner);
        const updatedWalkableDataLearner = { ...walkableDataLearner, pathways };

        const coursewareItemMap = await parseWalkableData({ ...walkable, ...updatedWalkableDataLearner });
        _saveCoursewareItemMapToState(coursewareItemMap);
        if (coursewareItemMap) {
          createBoundedListAndCreateListeners(coursewareItemMap);
        }
      })
      .catch((parsingError) => {
        throw new Error('Failed to parse learner ACTIVITY.', parsingError);
      });
  }

  async function fetchLearnerInteractive(interactiveId, walkable) {
    const { data } = await getInteractiveByIdLearner({
      cohortId: currentCohortId,
      deploymentId: currentDeploymentId,
      interactiveId,
    });

    try {
      const { interactive: walkableDataLearner } = data.learn.cohort.deployment[0];
      const isPreventConfigAssetResolve = featureFlagService.is(featureKeys.AUTHORING_PREVENT_CONFIG_ASSET_RESOLVE);
      const coursewareItemMap = await parseWalkableData({ ...walkable, ...walkableDataLearner, isPreventConfigAssetResolve });
      _saveCoursewareItemMapToState(coursewareItemMap);
      if (coursewareItemMap) {
        createBoundedListAndCreateListeners(coursewareItemMap);
      }
    } catch (parsingError) {
      throw new Error('Failed to parse learner INTERACTIVE.');
    }
  }

  function fetchLearnerInteractiveNew(interactiveId, walkable) {
    getInteractiveByIdLearner({
      cohortId: currentCohortId,
      deploymentId: currentDeploymentId,
      interactiveId,
    })
      .then(async (response) => {
        const { data } = response;
        const { interactive: walkableDataLearner } = data.learn.cohort.deployment[0];
        const isPreventConfigAssetResolve = featureFlagService.is(featureKeys.AUTHORING_PREVENT_CONFIG_ASSET_RESOLVE);
        const coursewareItemMap = await parseWalkableData({ ...walkable, ...walkableDataLearner, isPreventConfigAssetResolve });
        _saveCoursewareItemMapToState(coursewareItemMap);
        if (coursewareItemMap) {
          createBoundedListAndCreateListeners(coursewareItemMap);
        }
      })
      .catch((parsingError) => {
        throw new Error('Failed to parse learner INTERACTIVE.', parsingError);
      });
  }

  function containsNonFreePathway(walkable) {
    const pathways = walkable?.pathways || [];
    const nonFreePathways = pathways.find(((pathway) => pathway.type && pathway.type !== 'FREE'));
    if (nonFreePathways) {
      return true;
    }
    return false;
  }

  async function loadDeploymentDataNew({
    cohortId, deploymentId, elementId, courseId, unitId, lessonId, screenId, disableBrowserHistory,
  }) {
    currentDeploymentId = deploymentId;
    currentCohortId = cohortId;
    try {
      let data;
      const isPOCRTMEnabled = featureFlagService.is(featureKeys.LEARNSPACE_RTM_SOLUTION_1_POC);
      const isPOCV2Enabled = featureFlagService.is(featureKeys.LEARNSPACE_POC_V2);

      if (isPOCV2Enabled) {
        if (elementId) {
          data = await getStaticDataElementId({
            cohortId,
            deploymentId,
            elementId,
          });
        } else {
          data = await getStaticDataDefault({
            cohortId,
            deploymentId,
          });
        }
      } else if (isPOCRTMEnabled) {
        data = await getStaticDataFromSocket({
          cohortId,
          deploymentId,
          courseId,
          unitId,
          lessonId,
          screenId,
        });
      } else {
        data = await getStaticData({
          cohortId,
          deploymentId,
          courseId,
          unitId,
          lessonId,
          screenId,
        });
      }

      if (!data) {
        throw new Error('Failed to load deployment data.');
      }
      let rootWalkable;

      if (isPOCV2Enabled) {
        let elementWalkable;
        let walkables;

        if (elementId) {
          elementWalkable = data.learn.cohort.deployment[0]?.element || [];
          const elementAncestors = elementWalkable.ancestryWalkables || [];

          walkables = [elementWalkable, ...elementAncestors].reverse();
        } else {
          walkables = data.learn.cohort.deployment[0]?.activity?.getDefaultFirst || [];
          elementWalkable = data.learn.cohort.deployment[0]?.activity?.getDefaultFirst[0];
        }

        let itemsToSave = {};

        await Promise.allSettled(walkables.forEach(async (walkable, index) => {
          const coursewareItemMapWalkable = await parseWalkableData({ ...walkable });
          // To render the page without waiting for learner data, we need manually generate pathways to format expected by Plugins (such as standard lesson)
          // This can only be done for a walkable containing only free pathways. For any other pathway type, render is determined by learner data
          const hasNonFreePathway = containsNonFreePathway(walkable);
          if (!hasNonFreePathway && index < walkables.length - 1) {
            const generatedPathway = [{ itemId: walkables[index + 1].id }];
            coursewareItemMapWalkable[walkable.id].state.pathway = generatedPathway;
          }
          itemsToSave = { ...itemsToSave, ...coursewareItemMapWalkable };
        }));

        setProductId(data.learn.cohort.productId);
        // TODO: We should add a flag to root plugin to control this feature
        RouteProvider.initialize({ disableBrowserHistory });
        // Initialize subscription aggregator if there isn't one already
        if (!_subscriptionAggregation) {
          _subscriptionAggregation = new SubscriptionAggregation(deploymentId);
        }
        _saveCoursewareItemMapToState(itemsToSave);

        if (elementId) {
          handleLearnerDataElementId({
            cohortId,
            deploymentId,
            elementId,
            walkablesStatic: walkables,
          });
        } else {
          handleLearnerDataDefaultFirst({
            cohortId,
            deploymentId,
            walkablesStatic: walkables,
          });
        }
        return { coursewareItemId: walkables[0].id, plugin: walkables[0].plugin };
      }


      const { course: courseWalkable, unit: unitWalkable, activity: activitywalkable, interactive: interactiveWalkable } = data.learn.cohort.deployment[0];

      if (interactiveWalkable) {
        const coursewareItemMapInteractive = await parseWalkableData({ ...interactiveWalkable });
        rootWalkable = interactiveWalkable;
        _saveCoursewareItemMapToState(coursewareItemMapInteractive);
      }

      if (activitywalkable) {
        const coursewareItemMapActivity = await parseWalkableData({ ...activitywalkable });
        rootWalkable = activitywalkable;

        // TODO: if the pathway array is empty
        const activityId = rootWalkable.id;
        const interactiveId = interactiveWalkable?.id;
        if (interactiveId && coursewareItemMapActivity[activityId].state.pathway.length === 0) {
          coursewareItemMapActivity[activityId].state.pathway = [{ itemId: interactiveId }];
        }
        _saveCoursewareItemMapToState(coursewareItemMapActivity);
      }

      if (unitWalkable) {
        const coursewareItemMapUnit = await parseWalkableData({ ...unitWalkable });
        rootWalkable = unitWalkable;
        _saveCoursewareItemMapToState(coursewareItemMapUnit);
      }

      if (courseWalkable) {
        const coursewareItemMapCourse = await parseWalkableData({ ...courseWalkable });
        rootWalkable = courseWalkable;
        _saveCoursewareItemMapToState(coursewareItemMapCourse);
      }
      setProductId(data.learn.cohort.productId);
      // TODO: We should add a flag to root plugin to control this feature
      RouteProvider.initialize({ disableBrowserHistory });
      // Initialize subscription aggregator if there isn't one already
      if (!_subscriptionAggregation) {
        _subscriptionAggregation = new SubscriptionAggregation(deploymentId);
      }

      fetchLearnerActivityAndInteractive(
        cohortId,
        deploymentId,
        courseId,
        unitId,
        lessonId,
        screenId,
        courseWalkable,
        unitWalkable,
        activitywalkable,
        interactiveWalkable,
      );
      return { coursewareItemId: rootWalkable.id, plugin: rootWalkable.plugin };
    } catch (error) {
      const errorMessage = `Failed to load deploymentData111 ${cohortId}/${deploymentId}/${lessonId}/${screenId}`;
      throw new Error(errorMessage);
    }
  }

  /**
   * This combines learner activity and interactive learner graphql calls
   */
  async function fetchLearnerActivityAndInteractive(
    cohortId,
    deploymentId,
    courseId,
    unitId,
    lessonId,
    screenId,
    courseWalkable,
    unitWalkable,
    activityWalkable,
    interactiveWalkable,
  ) {
    let data;
    const isPOCRTMEnabled = featureFlagService.is(featureKeys.LEARNSPACE_RTM_SOLUTION_1_POC);
    if (isPOCRTMEnabled) {
      data = await getLearnerDataFromSocket({
        cohortId,
        deploymentId,
        courseId,
        unitId,
        lessonId,
        screenId,
      });
    } else {
      data = await getLearnerData({
        cohortId,
        deploymentId,
        courseId,
        unitId,
        lessonId,
        screenId,
      });
    }
    const { course: courseWalkableLearner, unit: unitWalkableLearner, activity: activitywalkableLearner, interactive: interactiveWalkableLearner } = data.learn.cohort.deployment[0];
    if (interactiveWalkableLearner) {
      const coursewareItemMapInteractive = await parseWalkableData({ ...interactiveWalkable, ...interactiveWalkableLearner });
      _saveCoursewareItemMapToState(coursewareItemMapInteractive);
      if (coursewareItemMapInteractive) {
        createBoundedListAndCreateListeners(coursewareItemMapInteractive);
      }
    }

    if (activitywalkableLearner) {
      const coursewareItemMapActivity = await parseWalkableData({ ...activityWalkable, ...activitywalkableLearner });
      _saveCoursewareItemMapToState(coursewareItemMapActivity);
      if (coursewareItemMapActivity) {
        createBoundedListAndCreateListeners(coursewareItemMapActivity);
      }
    }
    if (unitWalkableLearner) {
      const coursewareItemMapUnit = await parseWalkableData({ ...unitWalkable, ...unitWalkableLearner });
      _saveCoursewareItemMapToState(coursewareItemMapUnit);
      if (coursewareItemMapUnit) {
        createBoundedListAndCreateListeners(coursewareItemMapUnit);
      }
    }

    if (courseWalkableLearner) {
      const coursewareItemMapCourse = await parseWalkableData({ ...courseWalkable, ...courseWalkableLearner });
      _saveCoursewareItemMapToState(coursewareItemMapCourse);
      if (coursewareItemMapCourse) {
        createBoundedListAndCreateListeners(coursewareItemMapCourse);
      }
    }
  }

  async function handleLearnerDataElementId({
    cohortId,
    deploymentId,
    elementId,
    walkablesStatic,
  }) {
    try {
      const data = await getLearnerDataElementId({
        cohortId,
        deploymentId,
        elementId,
      });

      const elementWalkable = data.learn.cohort.deployment[0]?.element || [];
      const elementAncestors = elementWalkable.ancestryWalkables || [];
      const walkablesLearner = [elementWalkable, ...elementAncestors].reverse();

      walkablesLearner.forEach((walkableLearner) => {
        // TODO - Look into what this getUpdatedPathwaysOnProgressCompletion thing do
        // const updatedPathways = getUpdatedPathwaysOnProgressCompletion(elementWalkableLearner);
        const walkableStatic = walkablesStatic.find((walkable) => walkable.id === walkableLearner.id);
        const coursewareItemMapLocal = parseWalkableData({ ...walkableStatic, ...walkableLearner });

        _saveCoursewareItemMapToState(coursewareItemMapLocal);
        if (coursewareItemMapLocal) {
          createBoundedListAndCreateListeners(coursewareItemMapLocal);
        }
      });
    } catch (error) {
      const errorMessage = `Failed to load learner data for ${cohortId}/${deploymentId}/${elementId}`;
      throw new Error(errorMessage);
    }
  }

  async function handleLearnerDataDefaultFirst({
    cohortId,
    deploymentId,
    walkablesStatic,
  }) {
    try {
      const data = await getLearnerDataDefaultFirst({
        cohortId,
        deploymentId,
      });
      const walkablesLearner = data.learn.cohort.deployment[0].activity.getDefaultFirst;
      walkablesLearner.forEach((walkableLearner) => {
        // TODO - Look into what this getUpdatedPathwaysOnProgressCompletion thing do
        // const updatedPathways = getUpdatedPathwaysOnProgressCompletion(elementWalkableLearner);
        const walkableStatic = walkablesStatic.find((walkable) => walkable.id === walkableLearner.id);
        const coursewareItemMapLocal = parseWalkableData({ ...walkableStatic, ...walkableLearner });
        const walkableStudentScopeUrn = coursewareItemMapLocal[walkableLearner.id]?.scopeURN;
        if (walkableStudentScopeUrn) {
          _subscribeToStudentScope(walkableStudentScopeUrn);
        }
        _saveCoursewareItemMapToState(coursewareItemMapLocal);
        if (coursewareItemMapLocal) {
          createBoundedListAndCreateListeners(coursewareItemMapLocal);
        }
      });
    } catch (error) {
      const errorMessage = `Failed to load learner data for ${cohortId}/${deploymentId}`;
      throw new Error(errorMessage);
    }
  }

  // This is the function that is called to fetch the first activity of a deployment.
  async function loadDeploymentData({ cohortId, deploymentId, disableBrowserHistory }) {
    currentDeploymentId = deploymentId;
    currentCohortId = cohortId;
    const { data } = await getDefaultActivity({ cohortId, deploymentId });
    if (!data) {
      throw new Error('Failed to load deployment data.');
    }
    const { activity: walkable } = data.learn.cohort.deployment[0];
    const { id: coursewareItemId, plugin } = walkable;
    await fetchLearnerActivity(coursewareItemId, walkable);
    setProductId(data.learn.cohort.productId);

    // TODO: We should add a flag to root plugin to control this feature
    RouteProvider.initialize({ disableBrowserHistory });

    // Initialize subscription aggregator if there isn't one already
    if (!_subscriptionAggregation) {
      _subscriptionAggregation = new SubscriptionAggregation(deploymentId);
    }

    return { coursewareItemId, plugin };
  }

  async function getWalkableData(walkableId) {
    function isLoaded(id) {
      return _state.has(id) && fromJS(_state.get(id)).has(SCHEMA);
    }

    if (!isLoaded(walkableId)) {
      await loadWalkableData(walkableId);
    }

    return _state.get(walkableId);
  }

  async function loadWalkableData(walkableId, type) {
    let { type: walkableType } = type ? { type } : (await CoursewareTargetProvider.getWalkableType(walkableId) || { type: null });
    const isDynamicQueryNonBlockingEnabled = featureFlagService.is(featureKeys.LEARNSPACE_DYNAMIC_CALLS_NON_BLOCKING);
    try {
      // Fix for BRNT-34097: Since we are trying to save the state of the component from the
      // screen that was never loaded, the cache is empty, so we find the parent of the
      // component and load it before saving the scope of the component else the schema
      // and other properties will be undefined.
      if (walkableType === COMPONENT) {
        const response = await CoursewareTargetProvider.getCoursewareTypeAndAncestry(walkableId);
        /* eslint-disable no-param-reassign */
        walkableId = CoursewareTargetProvider.getImmediateParentId(response.ancestry);
        walkableType = CoursewareTargetProvider.getImmediateParentType(response.ancestry);
      }

      if (walkableType === ACTIVITY || !walkableType) {
        const response = await getActivityById({
          cohortId: currentCohortId,
          deploymentId: currentDeploymentId,
          activityId: walkableId,
        });
        const { activity: walkable } = response.data.learn.cohort.deployment[0];

        if (isDynamicQueryNonBlockingEnabled) {
          fetchLearnerActivityNew(walkableId, walkable);
        } else {
          await fetchLearnerActivity(walkableId, walkable);
        }
      }

      if (walkableType === INTERACTIVE) {
        const response = await getInteractiveById({
          cohortId: currentCohortId,
          deploymentId: currentDeploymentId,
          interactiveId: walkableId,
        });
        const { interactive: walkable } = response.data.learn.cohort.deployment[0];
        if (isDynamicQueryNonBlockingEnabled) {
          const coursewareItemMap = await parseWalkableData({ ...walkable });
          _saveCoursewareItemMapToState(coursewareItemMap);
          fetchLearnerInteractiveNew(walkableId, walkable);
        } else {
          await fetchLearnerInteractive(walkableId, walkable);
        }
      }
    } catch (parsingError) {
      const errorMessage = `Failed to load walkable ${walkableId}`;
      throw new Error(errorMessage);
    }
  }

  async function evaluateDeployment(interactiveToEvaluateId) {
    const evaluationValidity = getEvaluationValidity(interactiveToEvaluateId);
    const screenTitle = await getInCoursewareState(interactiveToEvaluateId, ['title']);

    if (!evaluationValidity.isValid) {
      const { piSession } = window;
      const piUserId = piSession.userId();
      const eventName = 'evaluationSkipped';
      const eventData = {
        coursewareId: interactiveToEvaluateId,
        type: 'getEvaluationValidity',
        piUserId,
        description: `evaluation skipped due to courseware ${interactiveToEvaluateId} does not match pathway walkable ${evaluationValidity.pathwayWalkableId}`,
        name: screenTitle,
      };
      // eslint-disable-next-line no-console
      console.error('invalid evaluation attempt', interactiveToEvaluateId);
      logNewrelicAddPageAction(eventName, eventData);
      recordEvent(eventName, eventData);
      return null;
    }

    try {
      const timestamp = TimestampManager.generateTimestamp();

      const { evaluationResult, errorMessage, clientId = '' } = await CoursewareItemResolver.sendEvaluateRequest(
        currentDeploymentId,
        interactiveToEvaluateId,
        timestamp,
        currentCohortId,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }
      const eventName = 'evaluationSuccess';
      const eventData = {
        coursewareId: interactiveToEvaluateId,
        description: 'BE evaluation success',
        name: screenTitle,
        clientId,
        timeId: timestamp,
        deploymentDetails: evaluationResult?.deployment, // deployment details -- { id, activityId, changeId, cohortId }
        evaluationId: evaluationResult?.id,
        attemptId: evaluationResult?.attemptId,
        interactiveComplete: evaluationResult?.interactiveComplete,
      };
      logNewrelicAddPageAction(eventName, eventData);
      recordEvent(eventName, eventData);
      silentLog(JSON.stringify({ type: eventName, eventData }));
      if (evaluationResult.interactiveComplete) {
        // FIXME: we don't currently have a way of re-fetching the progress of a completed interactive, without
        // triggering a new attempt being created. So the best we can do right now is set it directly ourselves
        _state.setIn([interactiveToEvaluateId, PROGRESS, 'completion'], { value: 1, confidence: 1 });
      }

      return evaluationResult;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error evaluating ${interactiveToEvaluateId}: ${error.message}`);
      gtmService.pushTelemetryPageError(httpErrorCodes.UNPROCESSABLE_ENTITY, error.message);

      if (error.message === ERROR_EVALUATION_ALREADY_COMPLETED) {
        ToastManager.notify({
          message: messages.evaluationError.defaultMessage,
          actions: [
            {
              text: 'OK',
              fn: () => {
                window.open('about:blank', '_self').close();
              },
            },
          ],
          waitForAction: true,
        });
      } else {
        ToastManager.notify({
          message: messages.defaultError.defaultMessage,
          actions: [
            {
              text: 'Retry',
              fn: () => {
                this.evaluateDeployment(interactiveToEvaluateId);
              },
            },
          ],
          waitForAction: true,
        });
      }
      const { piSession } = window;
      const piUserId = piSession.userId();
      const eventName = 'evaluationFailure';
      const eventData = { coursewareId: interactiveToEvaluateId, errorMessage: error.message, piUserId, description: 'BE evaluation failed', name: screenTitle };
      logNewrelicAddPageAction(eventName, eventData);
      recordEvent(eventName, eventData);
    }
    return null;
  }

  // Check the parent attempt of the interactive to check if it matches with the attempt of the
  // nearest activity
  function checkParentAttempt(interactiveId, evaluationResult = {}) {
    const ancestry = _state.getIn([interactiveId, 'ancestry']);
    const parentPathwayIndex = ancestry && ancestry.length > 0 && ancestry.findIndex((item) => item.elementId === evaluationResult.parentId);
    const parentActivityId = parentPathwayIndex > -1 ? ancestry[parentPathwayIndex + 1] : undefined;
    const parentActivityProgress = parentActivityId && _state.getIn([parentActivityId.elementId, 'progress']);
    const isParentAttemptIdSame = (parentActivityProgress?.attempt?.id && parentActivityProgress.attempt.id === evaluationResult.parentActivityAttemptId) || false;

    return isParentAttemptIdSame;
  }

  async function getEvaluationResult(interactiveToEvaluateId) {
    try {
      const { evaluationResult, errorMessage } = await CoursewareItemResolver.sendResultEvaluateRequest(
        currentDeploymentId,
        interactiveToEvaluateId,
        currentCohortId,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (evaluationResult?.completed) {
        // FIXME: we don't currently have a way of re-fetching the progress of a completed interactive, without
        // triggering a new attempt being created. So the best we can do right now is set it directly ourselves
        _state.setIn([interactiveToEvaluateId, PROGRESS, 'completion'], { value: 1, confidence: 1 });
      }

      const shouldSendEvaluationResult = checkParentAttempt(interactiveToEvaluateId, evaluationResult);
      if ((evaluationResult?.code === 422 && evaluationResult?.type === 'learner.evaluate.get.error') || !shouldSendEvaluationResult) {
        return [];
      }

      return evaluationResult;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error evaluating ${interactiveToEvaluateId}: ${error.message}`);
      gtmService.pushTelemetryPageError(httpErrorCodes.UNPROCESSABLE_ENTITY, error.message);

      ToastManager.notify({
        message: messages.defaultError.defaultMessage,
        actions: [
          {
            text: 'Retry',
            fn: () => {
              this.getEvaluationResult(interactiveToEvaluateId);
            },
          },
        ],
        waitForAction: true,
      });
    }
    return null;
  }

  async function restartActivity(activityId) {
    const response = await CoursewareItemResolver.sendRestartRequest(
      currentDeploymentId,
      activityId,
      currentCohortId,
    );
    clearStateByRelatedCourseware(activityId);
    await refreshProgressAndScore(activityId);
    publishEvent('activityRestartEvent');
    return response;
  }

  async function getCoursewareEntryPoint(coursewareItemId) {
    let entryPoint = _state.getIn([coursewareItemId, 'entryPoint']);

    if (!entryPoint) {
      await loadWalkableData(coursewareItemId);
      entryPoint = _state.getIn([coursewareItemId, 'entryPoint']);
    }
    return entryPoint;
  }

  async function getCourseWareItem(coursewareItemId) {
    await getWalkableData(coursewareItemId);
    const coursewareItem = fromJS(_state.get(coursewareItemId)).toJS();
    return coursewareItem;
  }

  async function getInCoursewareState(coursewareItemId, path = []) {
    await getWalkableData(coursewareItemId);
    const coursewareItem = fromJS(_state.get(coursewareItemId)).toJS();

    const { state } = coursewareItem;

    const schema = _state.getIn([coursewareItemId, SCHEMA]);

    const populatedWithDefaultsState = populateMissingSchemaValues(schema, fromJS(state), path);

    const statePart = fromJS(populatedWithDefaultsState).getIn(path);

    return isImmutable(statePart) ? statePart.toJS() : statePart;
  }

  async function getInCompletedCoursewareState(coursewareItemId, evaluationId, path = []) {
    await getWalkableData(coursewareItemId);
    const evaluation = await getCompletedWalkableByEvaluationId({
      cohortId: currentCohortId,
      deploymentId: currentDeploymentId,
      evaluationId,
    });

    const { scopeData } = getDataFromScope(coursewareItemId, evaluation.scope);
    const coursewareItem = fromJS(_state.get(coursewareItemId)).toJS();
    const { expandedConfig: config, state } = coursewareItem;
    const schema = _state.getIn([coursewareItemId, SCHEMA]);
    const assets = _state.getIn([coursewareItemId, ASSETS]);
    const stateConfigContent = config?.pathway?.pathwayType === 'FREE' && state ? state : config;
    const completedState = composeState(schema, stateConfigContent, scopeData, assets);

    return getIn(completedState, path, undefined);
  }

  async function getInCoursewareStateRaw(coursewareItemId, path = []) {
    await getWalkableData(coursewareItemId);

    const rawConfig = _state.getIn([coursewareItemId, RAW_CONFIG, ...path]);
    const scope = _state.getIn([coursewareItemId, SCOPE, ...path]);
    const schema = _state.getIn([coursewareItemId, SCHEMA, ...path]);
    const completedRawState = composeState(schema, rawConfig, scope);

    return completedRawState;
  }

  async function setInCoursewareState(coursewareItemId, path = [], value = null) {
    if (path.length) {
      _state.setIn([coursewareItemId, STATE, ...path], value);
    } else {
      const currentState = _state.getIn([coursewareItemId, STATE]);
      _state.setIn([coursewareItemId, STATE], value ? { ...currentState, ...value.toJS() } : currentState);
    }
    const state = _state.getIn([coursewareItemId, STATE]);

    const schema = _state.getIn([coursewareItemId, SCHEMA]) || (await getCourseWareItem(coursewareItemId))?.[SCHEMA];
    const ancestry = _state.getIn([coursewareItemId, ANCESTRY]) || (await getCourseWareItem(coursewareItemId))?.[ANCESTRY] || [];
    const elementType = _state.getIn([coursewareItemId, 'elementType']);
    const { scope: updatedScope } = decomposeState(schema, state);


    // find the interactive
    const interactiveId = elementType !== INTERACTIVE
      ? ancestry?.find((ancestorObj) => ancestorObj.elementType === INTERACTIVE)?.elementId
      : coursewareItemId;
    const interactive = _state.getIn([interactiveId]);

    // For scope binding purposes, a courseware could be registered to any ancestor activity.
    // When we set the scope of a courseware, we need to check if the courseware is also registered to any of its ancestor activities
    // if its registered to ancestor activities, we need to update the scope in the ancestor activity as well
    const ancestorActivities = ancestry.filter((ancestor) => ancestor.elementType === ACTIVITY);
    const ancestorScopes = ancestorActivities.reduce((accumulator, current) => {
      const localScope = _state.get(current?.elementId)?.scope || [];
      if (localScope.length) {
        return [...accumulator, ...localScope];
      }
      return accumulator;
    }, []);

    const coursewareItemInActivityScope = ancestorScopes.filter((registeredItem) => registeredItem.sourceId === coursewareItemId);
    const coursewareItemInInteractiveScope = interactive?.scope?.find((registeredItem) => registeredItem.sourceId === coursewareItemId);

    // Generate timestamp
    const timestamp = TimestampManager.generateTimestamp();
    // if its registered, update the coursewareItem in the activity scope

    coursewareItemInActivityScope.forEach((coursewareItemScope) => {
    // Save timestamp to TimestampManager
      TimestampManager.set(
        coursewareItemScope.sourceId,
        { timestamp, receivedResponseFromServer: false }
      );

      // Immediately save updated state to cache and notify plugin

      CoursewareItemResolver.setStudentScope(
        coursewareItemScope.sourceId,
        currentDeploymentId,
        coursewareItemScope.scopeURN,
        JSON.stringify(updatedScope),
        timestamp,
        currentCohortId,
      );
    });


    const scopeURN = coursewareItemInInteractiveScope?.scopeURN || _state.getIn([coursewareItemId]).scopeURN;

    // Save timestamp to TimestampManager
    TimestampManager.set(
      coursewareItemId,
      { timestamp, receivedResponseFromServer: false }
    );

    return CoursewareItemResolver.setStudentScope(
      coursewareItemId,
      currentDeploymentId,
      scopeURN,
      JSON.stringify(updatedScope),
      timestamp,
      currentCohortId,
    );
  }

  async function composePluginState(coursewareId, data) {
    // Create the coursewareItemMap for the current courseware
    const currentCourseware = fromJS(_state.get(coursewareId)).toJS();
    const coursewareItemMap = { [coursewareId]: currentCourseware };

    // Get the bounded reference list for the current courseware
    const boundedReferencesList = getBoundReferences(coursewareItemMap);

    // Variable to hold the config with bindings resolved
    let resolvedBindingForCurrentConfig = null;

    // Iterate bound references list to resolve references and update config
    Object.entries(boundedReferencesList).forEach(([field, value]) => {
      if (value.id && value.sourceId) {
        // Resolve ref
        const sourceId = boundedReferencesList[field].id;
        const targetId = boundedReferencesList[field].sourceId;
        resolvedBindingForCurrentConfig = resolveBoundReferenceByTypeAndSourceId(_state, CONFIG, sourceId, targetId);
      }
    });

    const schema = _state.getIn([coursewareId, SCHEMA]);
    const assets = _state.getIn([coursewareId, ASSETS]);
    const state = _state.getIn([coursewareId, STATE]);
    const config = resolvedBindingForCurrentConfig !== null ? resolvedBindingForCurrentConfig : _state.getIn([coursewareId, CONFIG]);

    Object.entries(schema).forEach(([field, value]) => {
      if (value.type === 'pathway') {
        config[field] = state[field];
      }
    });

    let updatedConfig = null;
    const isV2AssetSchema = getSchemaVersion(schema).major >= 2;
    const externalAssets = assets.filter((asset) => asset.urn.startsWith(URN_EXTERNAL) && JSON.stringify(config).includes(asset.urn));

    if (externalAssets.length) {
      updatedConfig = await mapExternalUrnToURL(externalAssets, JSON.stringify(config), isV2AssetSchema);
    } else {
      // assigning default config when assest urn and config urn doesn't match
      updatedConfig = config;
    }


    const newState = composeState(schema, updatedConfig || config, data, assets);
    const updatedCourseware = { ...currentCourseware, state: newState };
    return updatedCourseware;
  }

  async function _receiveStateChangeFromServer(response) {
    const { sourceId, data, id: timeId } = response.studentScope;
    const updatedCourseware = await composePluginState(sourceId, JSON.parse(data));

    const shouldStateSetOccur = !TimestampManager.isCachedTimestampLatest(sourceId, timeId);

    if (shouldStateSetOccur) {
      _state.set(sourceId, updatedCourseware);
    }
  }

  async function subscribeToStudentScopeUpdate({ sourceId, context, target, type }, callback) {
    if (!_state.has(sourceId)) {
      const { ancestry = [], type: sourceType } = await CoursewareTargetProvider.getCoursewareTypeAndAncestry(sourceId);
      if (sourceType === COMPONENT) {
        const coursewareItemID = CoursewareTargetProvider.getImmediateParentId(ancestry);
        await loadWalkableData(coursewareItemID);
      } else {
        await loadWalkableData(sourceId);
      }
    }

    if (target) {
      return _state.register([sourceId, target], callback, type, sourceId);
    }

    return _state.register([sourceId, 'state'].concat(context), callback, type, sourceId);
  }

  async function getInCoursewareProgress(coursewareItemId, path = []) {
    await getWalkableData(coursewareItemId);
    return _state.getIn([coursewareItemId, PROGRESS, ...path]);
  }

  async function subscribeToCoursewareProgress(coursewareItemId, callbackFn, path = [], coursewareId) {
    return _state.register([coursewareItemId, PROGRESS, ...path], callbackFn, undefined, coursewareId);
  }

  async function getInCoursewareTheme(coursewareItemId, path = []) {
    await getWalkableData(coursewareItemId);
    return _state.getIn([coursewareItemId, THEME, ...path]);
  }

  async function setInCoursewareTheme(coursewareItemId, theme, path = []) {
    return _state.setIn([coursewareItemId, THEME, ...path], theme);
  }

  async function subscribeToCoursewareTheme(coursewareItemId, callbackFn, path = [], coursewareId, coursewareChildId) {
    return _state.register([coursewareItemId, THEME, ...path], callbackFn, undefined, coursewareId, coursewareChildId);
  }

  async function _subscribeToStudentScope(studentScopeURN) {
    const key = `${currentDeploymentId}|${studentScopeURN}`;
    if (!_rtmUnsubscribeFunctions[key]) {
      _rtmUnsubscribeFunctions[key] = 'awaiting';
      _rtmUnsubscribeFunctions[key] = await CoursewareItemResolver.leanerStudentScopeSubscribe(
        studentScopeURN,
        _receiveStateChangeFromServer,
        _subscriptionAggregation,
      );
    }
  }

  async function _getScopeURN(coursewareItemId, path) {
    try {
      let pathwayId;
      let componentId;
      let typeOfProperty;
      await getWalkableData(coursewareItemId);
      const schema = _state.getIn([coursewareItemId, SCHEMA]);
      const propertyObject = selectPropertyObject(schema, path);

      if (propertyObject && propertyObject.type === 'list' && propertyObject.listType === COURSEWARE_ITEM) {
        componentId = _state.getIn([coursewareItemId, STATE, COMPONENTS, 0, 'itemId']);
        typeOfProperty = COURSEWARE_ITEM;
      } else if (propertyObject && propertyObject.type === PATHWAY) {
        pathwayId = _state.getIn([coursewareItemId, STATE, ...path, 'pathwayId']);
        componentId = _state.getIn([pathwayId, STATE, COMPONENTS, 0, 'itemId']);
        typeOfProperty = PATHWAY;
      }
      const studentScopeURN = _state.getIn([componentId || coursewareItemId, SCOPE_URN]);
      return { studentScopeURN, typeOfProperty };
    } catch (e) {
      throw new Error('couldnt get scopeURN');
    }
  }

  async function subscribeToCoursewareState(coursewareItemId, callbackFn, path = [], pluginId) {
    const statePart = await getInCoursewareState(coursewareItemId, path);

    const { studentScopeURN, typeOfProperty } = await _getScopeURN(coursewareItemId, path);
    const isPOCV2Enabled = featureFlagService.is(featureKeys.LEARNSPACE_POC_V2);

    if (isPOCV2Enabled) {
      if (typeOfProperty !== COURSEWARE_ITEM && typeOfProperty !== PATHWAY) {
        if (studentScopeURN) {
          _subscribeToStudentScope(studentScopeURN);
        }
        callbackFn(statePart);
        return _state.register([coursewareItemId, STATE, ...path], callbackFn, [], pluginId);
      }
    }

    if (studentScopeURN && typeOfProperty !== COURSEWARE_ITEM && typeOfProperty !== PATHWAY) {
      _subscribeToStudentScope(studentScopeURN);
      return _state.register([coursewareItemId, STATE, ...path], callbackFn, [], pluginId);
    }
    if (typeOfProperty === PATHWAY) {
      return _state.register([coursewareItemId, STATE, ...path], callbackFn, [], pluginId);
    }

    callbackFn(statePart);
    return () => { };
  }

  async function subscribeToCompletedCoursewareState(coursewareItemId, callbackFn, evaluationId, path = [], coursewareId) {
    const statePart = await getInCompletedCoursewareState(coursewareItemId, evaluationId, path, coursewareId);
    callbackFn(statePart);
    return () => { };
  }

  function getDeploymentContext() {
    return {
      cohortId: currentCohortId,
      deploymentId: currentDeploymentId,
    };
  }

  /**
   * We don't have subscription for score and progress, so manually re-fetch anything that might have changed
   */
  async function refreshProgressAndScore(coursewareId) {
    const { type, ancestry } = await CoursewareTargetProvider.getCoursewareTypeAndAncestry(coursewareId);
    if (WALKABLES.includes(type)) {
      loadWalkableData(coursewareId, type);
    }

    if (ancestry) {
      ancestry.forEach(({ elementType, elementId }) => {
        if (elementType === ACTIVITY) {
          loadWalkableData(elementId, elementType);
        }
      });
    }
  }

  /**
   * Clear the state of all children of the given coursewareId.
   */
  function clearStateByRelatedCourseware(coursewareId) {
    _state
      .keys()
      .filter((id) => {
        const ancestry = _state.getIn([id, ANCESTRY]) || [];
        return ancestry.find(({ elementId }) => coursewareId === elementId);
      })
      .forEach((id) => {
        // We could use a less destructive method by only removing scope paths,
        // but we're not currently handling parial state well, and this approach
        // makes things clear & simple.
        _state.remove([id]);
      });
  }

  function getProductId() {
    return _productId;
  }

  function setProductId(productId) {
    _productId = productId;
  }

  /**
   * 1. Form the bounded references list.
   * 2. For each reference subscribe to  listener : subscribeToStudentScopeUpdate.
   * 3. Resolve the reference with the actual value of the target.
   * 4. Generate a state by calling composeState with updatedConfig and updatedScope.
   * 5. Call _state.set to set the new state for the coursewareID.
   * 6. Call setInCoursewareState which makes a call to learner.student.scope.set.
   * @param coursewareItemMap
   */
  function createBoundedListAndCreateListeners(coursewareItemMap) {
    // Get list of value binded references from coursewareItemMap
    const boundedReferencesList = getBoundReferences(coursewareItemMap);
    // For each reference subscribe to subscribeToStudentScopeUpdate listener
    subscribeToListenersForBoundedReferenceList(boundedReferencesList);
  }

  /**
   * Subscribe to listener "subscribeToStudentScopeUpdate" for each bounded reference
   * @param  boundedReferencesList : Array of objects with all value binded objects.
   */
  function subscribeToListenersForBoundedReferenceList(boundedReferencesList) {
    const boundedList = [...boundedReferencesList];
    boundedList.forEach(({ sourceId, context, id }) => {
      // If present call subscribeToStudentScopeUpdate listener passing sourceId otherwise pass activity id to subscribeToStudentScopeUpdate call
      const data = _state.getIn([sourceId, STATE]);
      if (data !== null && data !== undefined) {
        subscribeToStudentScopeUpdate(
          { sourceId, context, type: VALUE_BIND_TYPE },
          () => resolveBoundReferences(id, sourceId)
        );
      } else {
        /*
        This else condition happens when the target element is not found in the state.
        This usually happens whent the bound reference target not located on the same page as the source.
        ie when the binding is between courseware located on different activities (eg different lesson, unit etc)
        In this stitutation, we need to find the activity the target belongs to and subscribe that activity's scope
        */
        const ancestryOfSourceCourseware = _state.getIn([id, 'ancestry']) || [];
        const ancestryOfSourceCoursewareActivityOnly = ancestryOfSourceCourseware.filter((ancestor) => ancestor.elementType === ACTIVITY);
        ancestryOfSourceCoursewareActivityOnly.forEach((ancestorCourseware) => {
          const ancestorCoursewareScope = _state.getIn([ancestorCourseware.elementId, 'scope']) || [];
          const containsBoundReferenceTarget = ancestorCoursewareScope.find((scopeItem) => scopeItem.sourceId === sourceId);
          if (containsBoundReferenceTarget) {
            subscribeToStudentScopeUpdate(
              { sourceId: ancestorCourseware.elementId, target: SCOPE, type: VALUE_BIND_TYPE },
              () => resolveBoundReferences(id, sourceId, ancestorCourseware.elementId)
            );
          }
        });
      }
    });
  }

  /**
   * The function resolves bound references in 'config' and 'scope' object to actual value of the target
   * @param  {string} sourceId :: the source id of the element containing the bound reference
   * @param  {string} targetId :: the target id of the element within the bound reference object
   * @param  {string} activityId
   */
  function resolveBoundReferences(sourceId, targetId, activityId) {
    const updatedConfig = resolveBoundReferenceByTypeAndSourceId(_state, CONFIG, sourceId, targetId, activityId);
    const updatedScope = resolveBoundReferenceByTypeAndSourceId(_state, SCOPE, sourceId, targetId, activityId);
    updateCoursewareState(updatedConfig, updatedScope, sourceId);
  }

  /**
   * This function updates state and calls setInCoursewareState function
   * @param {object} updatedConfig
   * @param {object} updatedScope
   * @param {string} sourceId
   */
  const updateCoursewareState = (updatedConfig, updatedScope, sourceId) => {
    const schema = _state.getIn([sourceId, SCHEMA]);
    const assets = _state.getIn([sourceId, ASSETS]);
    const newState = composeState(schema, updatedConfig, updatedScope, assets);
    // After we resolve the scope for the element with value binding, notify backend of scope update
    // Even though we call _state.set directly above this, calling setInCoursewareState below will not trigger _state.set twice.
    // This is because there is a check inside setInCoursewareState which will prevent unnecessary second state set.
    setInCoursewareState(sourceId, undefined, fromJS(newState));
  };

  function cleanPluginSubscriptions(coursewareId) {
    _state.cleanPluginSubscriptions(coursewareId);
    RouteProvider.unsubscribeByCoursewareId(coursewareId);
    unsubscribeEventByCoursewareId(coursewareId);
    HistoryProvider.cleanPluginSubscriptions(coursewareId);
  }

  function getEvaluationValidity(walkableId) {
    /*
    Workaround for BRNT-22583

    If the walkable to be evaluated is a child of a pathway, only allow evaluation of said walkable when the walkable id exists in the pathway children list.
    As consequence, an evaluation is valid when:
        - The walkable is child of a free pathway
        - The walkable is the current walkable in a non free pathway
    */

    const currentWalkable = _state.getIn([walkableId]);
    const parentWalkableId = currentWalkable?.ancestry[1]?.elementId;

    if (!parentWalkableId) return { isValid: true, pathwayWalkableId: undefined };

    const { parentPathwayName, parentPathwayType } = getParentPathwayNameAndType(walkableId);

    if (parentPathwayType === 'FREE') return { isValid: true, pathwayWalkableId: undefined };

    const parentWalkable = _state.getIn([parentWalkableId]);
    const parentPathwayChildren = parentWalkable.state[parentPathwayName];

    return { isValid: parentPathwayChildren[0].itemId === walkableId, pathwayWalkableId: parentPathwayChildren[0].itemId };
  }

  // This function returns the name and type of the pathway that sits under the supplied walkable
  function getParentPathwayNameAndType(walkableId) {
    const walkable = _state.getIn([walkableId]);

    const parentPathwayId = walkable?.ancestry[0]?.elementId;
    const parentWalkableId = walkable?.ancestry[1]?.elementId;

    // Case - No parent pathway
    if (!parentPathwayId) {
      return {
        parentPathwayName: null,
        parentPathwayType: null,
      };
    }

    const parentWalkable = _state.getIn([parentWalkableId]);
    const parentPathwayArray = Object.entries(parentWalkable.config).find(([, value]) => value?.pathwayId && value.pathwayId === parentPathwayId);

    return {
      parentPathwayName: parentPathwayArray[0],
      parentPathwayType: parentPathwayArray[1].pathwayType,
    };
  }

  return {
    _state, // Testing only
    getWalkableData,
    loadWalkableData,
    loadDeploymentData,
    loadDeploymentDataNew,
    evaluateDeployment,
    getEvaluationResult,
    restartActivity,
    getCoursewareEntryPoint,
    getInCoursewareState,
    getProductId,
    setInCoursewareState,
    subscribeToCoursewareState,
    getInCoursewareProgress,
    subscribeToCoursewareProgress,
    getInCoursewareTheme,
    setInCoursewareTheme,
    subscribeToCoursewareTheme,
    getDeploymentContext,
    getInCompletedCoursewareState,
    subscribeToCompletedCoursewareState,
    refreshProgressAndScore,
    clearStateByRelatedCourseware,
    getCourseWareItem,
    subscribeToStudentScopeUpdate,
    createBoundedListAndCreateListeners, // unit testing only
    _saveCoursewareItemMapToState, // unit testing only
    cleanPluginSubscriptions,
    getInCoursewareStateRaw,
  };
})();

window.intest = CoursewareItemProvider;

export default CoursewareItemProvider;
